/**
 * LogbookFullView - Full-width timeline view with Feed/Ledger/Map modes
 * This is the main "working surface" for viewing and managing log entries
 */

import React, { useState, useMemo } from 'react';
import { 
  FileText, List, Search, Filter, Printer, 
  Calendar, ChevronRight, Clock, MapPin, Anchor,
  Navigation, AlertTriangle, Cloud, Users,
  Eye, EyeOff, Download, ChevronDown
} from 'lucide-react';
import { Badge } from '../../../catalyst/badge';
import LogbookQuickActions from './LogbookQuickActions';
// import DailyChecksCard from './DailyChecksCard';
import LogbookPrintView from './LogbookPrintView';
import { MapView } from './LogbookViews';
// FT-356: register onBack with the shell-level MobileBottomBar via context.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';

const LogbookFullView = ({
  logEntries = [],
  activeVoyage,
  voyages = [],
  onAddEntry,
  onSelectVoyage,
  currentVessel,
  onBack
}) => {
  useSetBackHandler(onBack);
  // View modes - Ledger as default
  const [viewMode, setViewMode] = useState('ledger'); // 'feed', 'ledger', 'map'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedVoyageFilter, setSelectedVoyageFilter] = useState('all'); // 'all', 'active', or voyage ID
  const [showFilters, setShowFilters] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Quick action drawer state
  const [showQuickActions, setShowQuickActions] = useState(false);

  // Filter entries based on selections
  const filteredEntries = useMemo(() => {
    let filtered = [...logEntries];

    // Date filter
    if (selectedDate) {
      filtered = filtered.filter(entry => {
        const entryDate = new Date(entry.timestamp).toISOString().split('T')[0];
        return entryDate === selectedDate;
      });
    }

    // Voyage filter
    if (selectedVoyageFilter !== 'all') {
      if (selectedVoyageFilter === 'active') {
        filtered = filtered.filter(entry => entry.voyageId === activeVoyage?.id);
      } else {
        filtered = filtered.filter(entry => entry.voyageId === selectedVoyageFilter);
      }
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(entry => entry.category === categoryFilter);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(entry => 
        entry.summary?.toLowerCase().includes(query) ||
        entry.description?.toLowerCase().includes(query) ||
        entry.type?.toLowerCase().includes(query)
      );
    }

    // Sort by timestamp (newest first)
    return filtered.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [logEntries, selectedDate, selectedVoyageFilter, categoryFilter, searchQuery, activeVoyage]);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = new Set(logEntries.map(e => e.category).filter(Boolean));
    return Array.from(cats).sort();
  }, [logEntries]);

  // Get category icon
  const getCategoryIcon = (category) => {
    const icons = {
      navigation: Navigation,
      weather: Cloud,
      safety: AlertTriangle,
      crew: Users,
      general: FileText
    };
    const Icon = icons[category] || FileText;
    return <Icon className="h-4 w-4" />;
  };

  // Get category color
  const getCategoryColor = (category) => {
    const colors = {
      navigation: 'blue',
      weather: 'gray',
      safety: 'orange',
      crew: 'green',
      machinery: 'purple',
      general: 'slate'
    };
    return colors[category] || 'gray';
  };

  // Feed View Component - Compact timeline style
  const FeedView = () => (
    <div className="bg-white rounded-lg">
      {filteredEntries.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-500">No log entries found</p>
          <p className="text-sm text-gray-400 mt-1">Try adjusting your filters</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {filteredEntries.map((entry, index) => (
            <div key={entry.id} className="flex items-start gap-3 px-4 py-2 hover:bg-gray-50 transition-colors">
              {/* Time column */}
              <div className="flex-shrink-0 w-16 text-right">
                <p className="text-sm font-medium text-gray-900">
                  {new Date(entry.timestamp).toLocaleTimeString('en-GB', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </p>
                {(index === 0 || 
                  new Date(entry.timestamp).toDateString() !== 
                  new Date(filteredEntries[index - 1]?.timestamp).toDateString()) && (
                  <p className="text-xs text-gray-500">
                    {new Date(entry.timestamp).toLocaleDateString('en-GB', { 
                      day: 'numeric', 
                      month: 'short' 
                    })}
                  </p>
                )}
              </div>
              
              {/* Timeline line and dot */}
              <div className="flex flex-col items-center">
                <div className={`w-2 h-2 rounded-full mt-1.5 ${
                  entry.type === 'LET_GO' || entry.type === 'VOYAGE_START' ? 'bg-green-500' :
                  entry.type === 'ALL_FAST' || entry.type === 'VOYAGE_END' ? 'bg-orange-500' :
                  entry.category === 'safety' ? 'bg-red-500' :
                  entry.category === 'navigation' ? 'bg-blue-500' :
                  'bg-gray-400'
                }`} />
                {index < filteredEntries.length - 1 && (
                  <div className="w-0.5 flex-1 bg-gray-200 mt-1" />
                )}
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0 pb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm text-gray-900">{entry.summary}</span>
                  <Badge color={getCategoryColor(entry.category)} size="sm">
                    {entry.category}
                  </Badge>
                  {entry.type === 'LET_GO' && (
                    <Badge color="green" size="sm">DEPARTURE</Badge>
                  )}
                  {entry.type === 'ALL_FAST' && (
                    <Badge color="orange" size="sm">ARRIVAL</Badge>
                  )}
                </div>
                {entry.description && (
                  <p className="text-xs text-gray-600 mt-0.5">{entry.description}</p>
                )}
                {entry.position && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    <MapPin className="h-3 w-3 inline mr-1" />
                    {entry.position.lat}, {entry.position.lon}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Ledger View Component (for printing)
  const LedgerView = () => (
    <div className="bg-white rounded-lg border border-gray-300 print:border-black">
      <div className="p-4 border-b border-gray-300 print:border-black">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="font-bold text-lg">Official Log Book</h3>
            <p className="text-sm text-gray-600">{currentVessel?.name || 'Vessel'}</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium">
              {selectedDate ? 
                new Date(selectedDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) :
                'All Dates'
              }
            </p>
            <p className="text-xs text-gray-500">Entries: {filteredEntries.length}</p>
          </div>
        </div>
      </div>
      
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-300 print:border-black">
            <th className="text-left p-2 font-medium">Time</th>
            <th className="text-left p-2 font-medium">Category</th>
            <th className="text-left p-2 font-medium">Entry</th>
            <th className="text-left p-2 font-medium">Position</th>
            <th className="text-left p-2 font-medium">Officer</th>
          </tr>
        </thead>
        <tbody>
          {filteredEntries.map((entry, index) => (
            <tr key={entry.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
              <td className="p-2 align-top">
                {new Date(entry.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
              </td>
              <td className="p-2 align-top capitalize">{entry.category}</td>
              <td className="p-2">
                <div className="font-medium">{entry.summary}</div>
                {entry.description && (
                  <div className="text-xs text-gray-600 mt-1">{entry.description}</div>
                )}
              </td>
              <td className="p-2 align-top text-xs">
                {entry.position ? `${entry.position.lat}, ${entry.position.lon}` : '-'}
              </td>
              <td className="p-2 align-top">{entry.officer || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      
      <div className="p-4 border-t border-gray-300 print:border-black">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-600 mb-2">Master's Signature</p>
            <div className="border-b border-gray-400 w-48"></div>
          </div>
          <div>
            <p className="text-xs text-gray-600 mb-2">Date</p>
            <div className="border-b border-gray-400 w-48"></div>
          </div>
        </div>
      </div>
    </div>
  );


  return (
    <div className="space-y-4">
      {/* Daily Checks Card - Shows regulatory and custom daily checks */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
        <p className="text-yellow-800">Daily checks temporarily disabled</p>
      </div>
      
      {/* Header with filters and view toggle */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Left: Voyage filter chips */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setSelectedVoyageFilter('all')}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                selectedVoyageFilter === 'all' 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All Entries
            </button>
            {activeVoyage && (
              <button
                onClick={() => setSelectedVoyageFilter('active')}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${
                  selectedVoyageFilter === 'active' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                Active Voyage
              </button>
            )}
            {voyages.slice(0, 3).map(voyage => (
              <button
                key={voyage.id}
                onClick={() => setSelectedVoyageFilter(voyage.id)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  selectedVoyageFilter === voyage.id 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {voyage.departurePort} → {voyage.destinationPort}
              </button>
            ))}
          </div>

          {/* Right: View mode toggle */}
          <div className="flex items-center gap-2">
            <div className="bg-gray-100 rounded-lg p-1 flex">
              <button
                onClick={() => setViewMode('feed')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  viewMode === 'feed' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <List className="h-4 w-4 inline mr-1" />
                Feed
              </button>
              <button
                onClick={() => setViewMode('ledger')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  viewMode === 'ledger' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <FileText className="h-4 w-4 inline mr-1" />
                Ledger
              </button>
            </div>

            {viewMode === 'ledger' && (
              <button
                onClick={() => window.print()}
                className="px-3 py-1 bg-gray-800 text-white rounded-lg text-sm font-medium hover:bg-gray-900 flex items-center gap-1"
              >
                <Printer className="h-4 w-4" />
                Print
              </button>
            )}
          </div>
        </div>

        {/* Filters row */}
        <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap items-center gap-3">
          {/* Date picker */}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-400" />
            <input
              type="date"
              value={selectedDate || ''}
              onChange={(e) => setSelectedDate(e.target.value || null)}
              className="px-3 py-1 text-sm border border-gray-300 rounded-lg"
            />
            {selectedDate && (
              <button
                onClick={() => setSelectedDate(null)}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Clear
              </button>
            )}
          </div>

          {/* Category filter */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-1 text-sm border border-gray-300 rounded-lg"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          {/* Search */}
          <div className="flex-1 max-w-xs">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search entries..."
                className="w-full pl-9 pr-3 py-1 text-sm border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          <div className="text-sm text-gray-500">
            {filteredEntries.length} entries
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="bg-gray-50 rounded-lg p-4 min-h-[600px]">
        {viewMode === 'feed' && <FeedView />}
        {viewMode === 'ledger' && <LedgerView />}
        {viewMode === 'map' && <MapView />}
      </div>

      {/* Quick Actions Component */}
      <LogbookQuickActions 
        showQuickActions={showQuickActions}
        setShowQuickActions={setShowQuickActions}
      />
    </div>
  );
};

export default LogbookFullView;